import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { MANUAL_KEYS, reportDiffers, toNumber } from '../reportingFollowUpConstants';
import {
    AmountInput,
    BusinessesRow,
    EmptyCell,
    ReadonlyAmount,
    ReportedStatus,
    RowLabel,
    SectionCard,
    TrackingFields,
    ValuesTable,
} from './SectionParts';

const KEYS = MANUAL_KEYS.WITHHOLDING;

const WithholdingSection = ({
    books,
    periodLabel,
    fileNumber,
    reportingType,
    tracking,
    onManualChange,
    onCopy,
    isReported,
    onReport,
    reportDisabled,
    onTrackingChange,
    direction,
}) => {
    const { t } = useTranslation();
    const manual = tracking.manualValues || {};
    const reportToPay = toNumber(manual[KEYS.reportTotalWithholding]) + toNumber(manual[KEYS.reportSalaryWithholding]);

    const manualInput = (key, booksValue) => (
        <AmountInput
            value={manual[key]}
            onChange={(value) => onManualChange(key, value)}
            mismatch={booksValue !== undefined && reportDiffers(booksValue, manual[key])}
        />
    );

    return (
        <SectionCard title={t('FollowUpWithholdingTitle')} fileNumber={fileNumber} reportingType={reportingType}>
            <ReportedStatus isReported={isReported} onReport={onReport} disabled={reportDisabled} />
            <ValuesTable periodLabel={periodLabel} columns={[t('FollowUpBooks'), t('FollowUpReport')]}>
                <RowLabel>{t('FollowUpNumberOfRecipients')}</RowLabel>
                <ReadonlyAmount value={books ? books.numberOfRecipients : null} />
                {manualInput(KEYS.reportRecipients, books?.numberOfRecipients ?? null)}

                <RowLabel>{t('FollowUpTotalPayments')}</RowLabel>
                <ReadonlyAmount value={books ? books.totalPayments : null} />
                {manualInput(KEYS.reportTotalPayments, books?.totalPayments ?? null)}

                <RowLabel>{t('FollowUpTotalWithholding')}</RowLabel>
                <ReadonlyAmount value={books ? books.totalWithholding : null} />
                {manualInput(KEYS.reportTotalWithholding, books?.totalWithholding ?? null)}

                <RowLabel>{t('FollowUpTotalSalaries')}</RowLabel>
                <EmptyCell />
                {manualInput(KEYS.reportTotalSalaries)}

                <RowLabel>{t('FollowUpSalaryWithholding')}</RowLabel>
                <EmptyCell />
                {manualInput(KEYS.reportSalaryWithholding)}

                <RowLabel>{t('FollowUpTotalWithholdingToPay')}</RowLabel>
                <ReadonlyAmount value={books ? books.totalWithholding : null} total />
                <ReadonlyAmount value={reportToPay} total />

                <BusinessesRow count={1} onCopy={books ? onCopy : undefined} />
            </ValuesTable>

            <TrackingFields tracking={tracking} onChange={onTrackingChange} direction={direction} />
        </SectionCard>
    );
};

WithholdingSection.propTypes = {
    books: PropTypes.shape({
        numberOfRecipients: PropTypes.number,
        totalPayments: PropTypes.number,
        totalWithholding: PropTypes.number,
    }),
    periodLabel: PropTypes.string,
    fileNumber: PropTypes.string,
    reportingType: PropTypes.string,
    tracking: PropTypes.object.isRequired,
    onManualChange: PropTypes.func.isRequired,
    onCopy: PropTypes.func.isRequired,
    isReported: PropTypes.bool,
    onReport: PropTypes.func,
    reportDisabled: PropTypes.bool,
    onTrackingChange: PropTypes.func.isRequired,
    direction: PropTypes.string,
};

export default WithholdingSection;
